import { useMemo } from 'react';
import { useQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../clients/axiosClient';
import type { UserRetailAccount, RetailAccountContent, PerformanceHistoryRange, PerformanceHistoryResponse } from '@shared/types';
import { retailAccountKeys } from './queryKeys';
import { deriveInitialPrincipal } from '../pages/portfolio/portfolioUtils';

export function useRetailAccounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: retailAccountKeys.all(),
    queryFn: async () => {
      const { data } = await axiosClient.get<{ accounts: UserRetailAccount[] }>(
        '/api/user-retail-accounts',
      );
      return (data.accounts ?? []) as UserRetailAccount[];
    },
    enabled: options?.enabled !== false,
  });
}

export function useAccountContent(accountId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: retailAccountKeys.content(accountId ?? ''),
    queryFn: async () => {
      const { data } = await axiosClient.get<{ content: RetailAccountContent }>(
        `/api/user-retail-accounts/${accountId}/account-content`,
      );
      return data.content as RetailAccountContent | undefined;
    },
    enabled: !!accountId && options?.enabled !== false,
  });
}

export function useAccountContentQueries(
  accounts: UserRetailAccount[],
  enabledIds: Set<string>,
) {
  return useQueries({
    queries: accounts.map((account) => ({
      queryKey: retailAccountKeys.content(account.id),
      queryFn: async () => {
        const { data } = await axiosClient.get<{ content: RetailAccountContent }>(
          `/api/user-retail-accounts/${account.id}/account-content`,
        );
        return data.content as RetailAccountContent | undefined;
      },
      enabled: enabledIds.has(account.id),
    })),
  });
}

export interface PerformanceHistoryAnchor {
  tradingStartedAt?: string;
  initialPrincipal?: number;
}

export function useAccountHistory(
  accountId: string | null,
  range: PerformanceHistoryRange,
  anchor?: PerformanceHistoryAnchor,
  enabled = true,
) {
  return useQuery({
    queryKey: retailAccountKeys.history(accountId ?? '', range),
    queryFn: async () => {
      const params: Record<string, string> = { range };
      if (anchor?.tradingStartedAt) params.tradingStartedAt = anchor.tradingStartedAt;
      if (anchor?.initialPrincipal !== undefined && anchor.initialPrincipal > 0) {
        params.initialPrincipal = String(anchor.initialPrincipal);
      }
      const { data } = await axiosClient.get<PerformanceHistoryResponse>(
        `/api/user-retail-accounts/${accountId}/performance-history`,
        { params },
      );
      return data;
    },
    enabled: !!accountId && enabled,
  });
}

export function useAccountHistoryQueries(
  accounts: UserRetailAccount[],
  contentById: Record<string, RetailAccountContent | undefined>,
  range: PerformanceHistoryRange,
) {
  return useQueries({
    queries: accounts.map((account) => {
      const content = contentById[account.id];
      const tradingStartedAt = content?.tradingStartedAt ?? content?.trackRecordStartedAt;
      const initialPrincipal = deriveInitialPrincipal(content);
      const params: Record<string, string> = { range };
      if (tradingStartedAt) params.tradingStartedAt = tradingStartedAt;
      if (initialPrincipal != null && initialPrincipal > 0) {
        params.initialPrincipal = String(initialPrincipal);
      }
      return {
        queryKey: retailAccountKeys.history(account.id, range),
        queryFn: async () => {
          const { data } = await axiosClient.get<PerformanceHistoryResponse>(
            `/api/user-retail-accounts/${account.id}/performance-history`,
            { params },
          );
          return data;
        },
        enabled: !!content,
      };
    }),
  });
}

export function usePatchAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      axiosClient.patch(`/api/user-retail-accounts/${id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.all() });
    },
  });
}

export function useRebalanceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      axiosClient.post(`/api/user-retail-accounts/${accountId}/rebalance`),
    onSuccess: (_data, accountId) => {
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.all() });
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.content(accountId) });
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.historyAll(accountId) });
    },
  });
}

export function useConnectAlpacaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      broker: string;
      endpoint: string;
      isPaper: boolean;
      nickname?: string;
      credentials: { apiKey: string; secretKey: string };
    }) => axiosClient.post<{ message: string }>('/api/user-retail-accounts', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.all() });
    },
  });
}

export function useContentById(accounts: UserRetailAccount[], enabledIds: Set<string>) {
  const contentQueries = useAccountContentQueries(accounts, enabledIds);

  const contentById = useMemo(() => {
    const map: Record<string, RetailAccountContent | undefined> = {};
    accounts.forEach((acc, i) => { map[acc.id] = contentQueries[i]?.data; });
    return map;
  }, [accounts, contentQueries]);

  const contentLoadingById = useMemo(() => {
    const map: Record<string, boolean> = {};
    accounts.forEach((acc, i) => { map[acc.id] = !!contentQueries[i]?.isFetching; });
    return map;
  }, [accounts, contentQueries]);

  const contentErrorById = useMemo(() => {
    const map: Record<string, string | null> = {};
    accounts.forEach((acc, i) => {
      const q = contentQueries[i];
      map[acc.id] = q?.error
        ? ((q.error as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load')
        : null;
    });
    return map;
  }, [accounts, contentQueries]);

  return { contentById, contentLoadingById, contentErrorById };
}
